import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useRestaurantStore } from "@/stores/restaurantStore";
import type { AppRole } from "@/lib/auth/roles";
import { Spinner } from "@/components/ui/spinner";
import {
  allowedRolesForPath,
  getDefaultLandingForRole,
} from "@/lib/auth/routePermissions";
import { useProfile } from "@/rc/hooks/profile/use-profile";

type RoleGuardProps = {
  allowed?: readonly AppRole[];
};

/**
 * Layer 3: restricts nested routes by active membership role in the store.
 */
export function RoleGuard({ allowed }: RoleGuardProps) {
  const { pathname } = useLocation();
  const { isSuperAdmin } = useProfile();
  const role = useRestaurantStore((s) => s.activeRole);
  const allowedRoles = allowed ?? allowedRolesForPath(pathname);

  if (isSuperAdmin) {
    return <Outlet />;
  }

  if (role === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner className="text-muted-foreground" />
        <span className="sr-only">Loading role</span>
      </div>
    );
  }

  if (!allowedRoles.includes(role)) {
    const pathToNavigate = getDefaultLandingForRole(role);
    return <Navigate to={pathToNavigate} replace />;
  }

  return <Outlet />;
}
